import { Button, Text } from "@ttc3k/trekker";
import { Flex } from "styled-system/jsx";
import { useTripperDebugPanel } from "./useTripperDebugPanel";
import { useTranslation } from "react-i18next";

type RowStatus = "pass" | "fail" | "skipped" | "warn" | "loading";

const STATUS_ICON: Record<RowStatus, string> = {
  pass: "\u2705",
  fail: "\u274C",
  skipped: "\u2796",
  warn: "\u26A0\uFE0F",
  loading: "\u23F3",
};

function StatusRow({
  label,
  status,
  detail,
}: {
  label: string;
  status: RowStatus;
  detail?: string;
}) {
  return (
    <Flex gap="100" alignItems="flex-start">
      <Text
        visual="bodySmall"
        style={{ flexShrink: 0, width: "20px", textAlign: "center" }}
      >
        {STATUS_ICON[status]}
      </Text>
      <Flex flexDir="column" gap="0">
        <Text visual="bodySmall" fontWeight="medium">
          {label}
        </Text>
        {detail && (
          <Text visual="bodySmall" color="text.mid" wordBreak="break-all">
            {detail}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

function clientCheckStatus(ok: boolean): "pass" | "fail" {
  return ok ? "pass" : "fail";
}

function serverStatus(
  s: "pass" | "fail" | "skipped",
): "pass" | "fail" | "skipped" {
  return s;
}

interface TripperDebugPanelProps {
  widgetId: string;
  accessId: string;
}

export const TripperDebugPanel = ({
  widgetId,
  accessId,
}: TripperDebugPanelProps) => {
  const { t } = useTranslation("tripper", { keyPrefix: "WIDGET.DEBUG" });
  const {
    loading,
    clientChecks,
    networkChecks,
    serverResult,
    serverError,
    copyReport,
  } = useTripperDebugPanel(widgetId, accessId);

  return (
    <Flex
      part="tripper__debug-panel"
      flexDir="column"
      gap="200"
      p="300"
      border="1px solid"
      borderColor="border.light"
      borderRadius="100"
      bg="bg.lightest"
      w="full"
      style={{ flexShrink: 0 }}
    >
      <Text visual="bodyMedium" fontWeight="bold">
        {t("TITLE")}
      </Text>

      {/* Client checks */}
      <Text visual="bodySmall" fontWeight="bold" color="text.mid">
        {t("SECTION_CLIENT")}
      </Text>

      <StatusRow
        label={t("ELEMENT_REGISTERED")}
        status={clientCheckStatus(clientChecks.elementRegistered)}
      />
      <StatusRow
        label={t("WIDGET_ID_PRESENT")}
        status={clientCheckStatus(clientChecks.widgetIdPresent)}
        detail={clientChecks.widgetIdPresent ? widgetId : undefined}
      />
      <StatusRow
        label={t("WIDGET_ID_FORMAT")}
        status={clientCheckStatus(clientChecks.widgetIdFormat)}
      />
      <StatusRow
        label={t("ACCESS_ID_PRESENT")}
        status={clientCheckStatus(clientChecks.accessIdPresent)}
        detail={clientChecks.accessIdPresent ? accessId : undefined}
      />
      <StatusRow
        label={t("ACCESS_ID_FORMAT")}
        status={clientCheckStatus(clientChecks.accessIdFormat)}
      />
      <StatusRow
        label={t("REFERRER")}
        status={clientChecks.referrerAvailable ? "pass" : "warn"}
        detail={clientChecks.referrer}
      />
      <StatusRow
        label={t("PAGE_URL")}
        status="pass"
        detail={clientChecks.pageUrl}
      />
      {clientChecks.inIframe && (
        <StatusRow label={t("IN_IFRAME")} status="warn" />
      )}

      {/* Network reachability */}
      <Text visual="bodySmall" fontWeight="bold" color="text.mid" mt="100">
        {t("SECTION_NETWORK")}
      </Text>

      <StatusRow
        label={t("API_REACHABLE")}
        status={networkChecks.apiReachable}
        detail={networkChecks.apiDetail ?? undefined}
      />
      <StatusRow
        label={t("CDN_REACHABLE")}
        status={networkChecks.cdnReachable}
        detail={networkChecks.cdnDetail ?? undefined}
      />
      <StatusRow
        label={t("MAPBOX_REACHABLE")}
        status={networkChecks.mapboxReachable}
        detail={networkChecks.mapboxDetail ?? undefined}
      />

      {/* Server checks */}
      <Text visual="bodySmall" fontWeight="bold" color="text.mid" mt="100">
        {t("SECTION_SERVER")}
      </Text>

      {loading && (
        <Text visual="bodySmall" color="text.mid">
          {t("LOADING")}
        </Text>
      )}

      {serverError && (
        <StatusRow
          label={t("SERVER_UNREACHABLE")}
          status="fail"
          detail={serverError}
        />
      )}

      {serverResult && (
        <>
          <StatusRow
            label={t("OVERALL")}
            status={serverResult.ok ? "pass" : "fail"}
          />
          <StatusRow
            label={t("BEARER_FORMAT")}
            status={serverStatus(serverResult.checks.bearerFormat)}
          />
          <StatusRow
            label={t("METADATA")}
            status={serverStatus(serverResult.checks.metadataValid)}
          />
          <StatusRow
            label={t("WA_TYPE")}
            status={serverStatus(serverResult.checks.waTypeKnown)}
          />
          <StatusRow
            label={t("ORG_FOUND")}
            status={serverStatus(serverResult.checks.organizationFound)}
          />
          <StatusRow
            label={t("DOMAIN_MATCH")}
            status={serverStatus(serverResult.checks.domainMatch)}
            detail={
              serverResult.checks.matchedSource
                ? `${t("MATCHED_VIA")}: ${serverResult.checks.matchedSource}`
                : serverResult.checks.origin ||
                    serverResult.checks.referer ||
                    serverResult.checks.embedUrl
                  ? `${t("SOURCES_RECEIVED")}: ${[
                      serverResult.checks.origin &&
                        `origin=${serverResult.checks.origin}`,
                      serverResult.checks.referer &&
                        `referer=${serverResult.checks.referer}`,
                      serverResult.checks.embedUrl &&
                        `embed-url=${serverResult.checks.embedUrl}`,
                    ]
                      .filter(Boolean)
                      .join(", ")}`
                  : t("NO_SOURCES")
            }
          />
          {serverResult.checks.widgetProvided && (
            <>
              <StatusRow
                label={t("WIDGET_FOUND")}
                status={serverStatus(serverResult.checks.widgetFound)}
              />
              <StatusRow
                label={t("WIDGET_OWNER")}
                status={serverStatus(
                  serverResult.checks.widgetOwnerMatchesOrg,
                )}
              />
              {serverResult.checks.widgetPaused !== "skipped" && (
                <StatusRow
                  label={t("WIDGET_PAUSED")}
                  status={serverStatus(serverResult.checks.widgetPaused)}
                />
              )}
            </>
          )}

          {serverResult.hint && (
            <Flex
              p="200"
              bg="bg.wa.lightest"
              borderRadius="100"
              mt="100"
            >
              <Text visual="bodySmall">
                {serverResult.supportCode && (
                  <Text as="span" visual="bodySmall" fontWeight="bold">
                    [{serverResult.supportCode}]{" "}
                  </Text>
                )}
                {serverResult.hint}
              </Text>
            </Flex>
          )}
        </>
      )}

      <Flex mt="100">
        <Button
          size="sm"
          visual="primary"
          mode="light"
          onClick={copyReport}
        >
          {t("COPY_REPORT")}
        </Button>
      </Flex>
    </Flex>
  );
};
